import { Component, OnInit } from '@angular/core';
import { EventEmitter, Output  } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { languageService, languageSupport } from '@unpispas/upp-base';
import { stateService } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';
import { identificationService } from '@unpispas/upp-base';
import { loginService } from '@unpispas/upp-data';

/**
 * Credentials accepted by the login page to prefill username/password
 * (`SetCredentials`). Owned here — registry and shell only produce/forward
 * values that match this contract.
 */
export interface LoginCredentials {
    username: string;
    password: string;
}

@Component({
    selector: 'upp-login-page',
    templateUrl: './upp-login.html',
    styleUrls: [
      './upp-login.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UppLoginPageComponent extends languageSupport implements OnInit {
    @Output() Recovery = new EventEmitter<void>();
    @Output() Registry = new EventEmitter<void>();

    constructor(
        private lang: languageService,
        private change: ChangeDetectorRef,
        private login: loginService,
        private device: identificationService,
        private state: stateService,
        public view: viewService,
    ){
        super(lang, change);
    }

    /** True when the Pinpad shell is running (`state.WorkMode === 'PTF'`). */
    get IsPinpad(): boolean {
        return this.state.WorkMode === 'PTF';
    }

    private _newdevice = false;
    async ngOnInit(): Promise<void> {
        this._newdevice = !(await this.device.IsKnown());
        this.change.markForCheck();
    }

    get NewDevice(): boolean {
        return this._newdevice;
    }

    private _loginForm: FormGroup | null = null;
    get LoginForm(){
        if (this._loginForm == null){
            const _kiosk = this.view.Kiosk;

            this._loginForm = new FormGroup({
                username: new FormControl(null, [
                    Validators.required,
                    Validators.email
                ]),
                password: new FormControl(null, [
                    Validators.required,
                    Validators.minLength(8)
                ]),
                deviceid: new FormControl(null),
                remember: new FormControl(false, [
                    // no validators
                ]),
                usekiosk: new FormControl(_kiosk, [
                    // no validators
                ])
            });

            if (this.NewDevice){
                this._loginForm.get('deviceid')?.setValidators([Validators.required]);
            }
        }

        return this._loginForm;
    }

    get CanSubmit(): boolean {
        if (!this._loginForm || !this._loginForm.valid){
            return false;
        }

        if (this._newdevice){
            const _device = this._loginForm.get('deviceid');
            if (_device && _device.valid){
                return !!String(_device.value || '').trim();
            }
            return false;
        }

        return true;
    }

    private _loginerror = false;
    get LoginError(): boolean {
        return this._loginerror;
    }

    set LoginError(value: boolean){
        if (value != this._loginerror){
            this._loginerror = value;
            this.change.markForCheck();
        }
    }

    /**
     * True when the last failed login was refused because the device id already
     * has a live SESSION on another client (`Exists`).
     */
    get DeviceBusy(): boolean {
        return this._loginerror && (this.login.LastError === loginService.ErrorExists);
    }

    SetCredentials(credentials: LoginCredentials): void {
        this.LoginForm.patchValue({
            username: credentials.username,
            password: credentials.password
        });

        this.LoginError = false;
        this.change.markForCheck();
    }

    async DoLogin() : Promise <void> {
        const _username: string | null = this.LoginForm.get('username')?.value || null;
        const _password: string | null = this.LoginForm.get('password')?.value || null;
        const _deviceid: string | null = this.LoginForm.get('deviceid')?.value || null;
        const _remember: boolean = this.IsPinpad
            ? true
            : (this.LoginForm.get('remember')?.value || false);

        if ((!_username || !_password) || (this._newdevice && !_deviceid)){
            return;
        }

        let _error = false;
        let _logok = false;

        try {
            if (this._newdevice){
                await this.device.TryDevice(String(_deviceid || '').trim());
            }

            let _success = false;
            _success = _success || await this.login.DoLogin(_username, _password, _remember, false);
            _success = _success || await this.login.DoLogin(_username, _password, _remember, true);

            if (_success){
                _logok = true;

                if (this._newdevice){
                    await this.device.UseDevice(false);
                    this._newdevice = false;
                }

                this.LoginForm.reset();
                this.view.View = 'USER';
                this.LoginError = false;
            }
            else {
                _error = true;
            }
        }
        catch {
            _error = true;
        }
        finally {
            if (_error){
                if (this._newdevice){
                    await this.device.UseDevice(true);

                    if (_logok){
                        window.location.reload();
                    }
                }

                this.LoginError = true;
            }
        }
    }

    ResetError(){
        this.LoginError = false;
    }

    OnKiosk(event: any){
        this.view.Kiosk = event.detail.checked;
    }

    OpenRecover(){
        this.Recovery.emit();
    }

    OpenRegister(){
        this.Registry.emit();
    }
}
